import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Users, TrendingDown, Ship, ArrowRight, Check, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Landing() {
  const navigate = useNavigate();
  const [containers, setContainers] = useState([]);
  const scrollContainerRef = useRef(null);
  
  // Fetch active containers
  useEffect(() => {
    const fetchContainers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/containers/active`);
        setContainers(response.data || []);
      } catch (error) {
        console.error('Error fetching containers:', error);
      }
    };
    fetchContainers();
  }, []);

  // Scroll handlers for container carousel
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Use first container for main display, or defaults
  const mainContainer = containers[0] || {};
  const containerFilled = mainContainer.current_fill || 0;
  const containerCapacity = mainContainer.total_capacity || 27500;
  const fillPercentage = (containerFilled / containerCapacity) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      {/* Hero Section with Container Visualization */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          {/* Headline */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight text-gray-900 mb-4">
              Buy Flooring at Bulk Prices — <span className="text-primary">Together.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Pool your order with other builders to unlock wholesale pricing on LVP & Engineered Hardwood. 
              You only pledge SQFT — pay when the container ships.
            </p>
          </div>

          {/* Container Visualization with Real Image */}
          <div className="relative mb-12">
            <div className="bg-gradient-to-b from-gray-700 to-gray-800 rounded-3xl shadow-2xl p-6 md:p-8 overflow-hidden">
              {/* Header Label */}
              <div className="absolute top-4 left-4 z-20">
                <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                  <p className="text-primary font-bold text-sm">BlueCrate</p>
                  <p className="text-gray-600 text-xs">ACTIVE CONTAINERS</p>
                </div>
              </div>

              {/* Container Image */}
              <div className="relative mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bulk-builders/artifacts/feymx1zn_A09ED9BC-8B34-4CE2-B578-3361D7A7F15F.jpeg" 
                  alt="BlueCrate Container" 
                  className="w-full max-w-xl mx-auto rounded-xl"
                />
              </div>

              {/* Scrollable Container Cards */}
              {containers.length > 0 && (
                <div className="relative">
                  {/* Left Arrow */}
                  <button 
                    onClick={scrollLeft}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all"
                    data-testid="scroll-left"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-700" />
                  </button>

                  {/* Container Cards */}
                  <div 
                    ref={scrollContainerRef}
                    className="flex gap-3 overflow-x-auto scrollbar-hide px-10 py-2 scroll-smooth"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                  >
                    {containers.map((container, idx) => {
                      const fill = container.current_fill || 0;
                      const capacity = container.total_capacity || 27500;
                      const pct = Math.round((fill / capacity) * 100);
                      const containerNum = container.container_number?.slice(-5) || `#${idx + 1}`;
                      
                      // Check for shipped/delivered status (exact values from STATUS_FLOW)
                      const shippedStatuses = ['arrived', 'unloaded', 'customs_cleared', 'on_rail', 'out_for_delivery', 'delivered', 'shipped', 'complete', 'completed'];
                      const transitStatuses = ['at_port', 'on_vessel', 'in_transit'];
                      const productionStatuses = ['po_sent', 'in_production', 'production_complete', 'production', 'manufacturing'];
                      
                      const status = (container.container_status || container.status || '').toLowerCase();
                      
                      const isShipped = shippedStatuses.includes(status);
                      const isInTransit = transitStatuses.includes(status);
                      const isProduction = productionStatuses.includes(status);
                      
                      return (
                        <div 
                          key={container.container_id || idx}
                          className={`flex-shrink-0 backdrop-blur-sm rounded-xl p-3 min-w-[140px] shadow-lg ${
                            isShipped ? 'bg-green-100/95' : isInTransit ? 'bg-blue-100/95' : 'bg-white/95'
                          }`}
                          data-testid={`container-card-${idx}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-primary">#{containerNum}</span>
                            {isShipped ? (
                              <span className="text-xs font-bold text-green-600 bg-green-200 px-2 py-0.5 rounded-full">DELIVERED</span>
                            ) : isInTransit ? (
                              <span className="text-xs font-bold text-blue-600 bg-blue-200 px-2 py-0.5 rounded-full">IN TRANSIT</span>
                            ) : isProduction ? (
                              <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">IN PRODUCTION</span>
                            ) : (
                              <span className="text-sm font-bold text-gray-900">{pct}%</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            {isShipped ? 'Delivered to customers' : 
                             isInTransit ? 'Shipping to destination' :
                             `Filled: ${fill.toLocaleString()}/${capacity.toLocaleString()}`}
                          </div>
                          {/* Progress Bar */}
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                isShipped ? 'bg-green-500' : 
                                isInTransit ? 'bg-blue-500' :
                                pct >= 80 ? 'bg-green-500' : 'bg-primary'
                              }`}
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right Arrow */}
                  <button 
                    onClick={scrollRight}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/80 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all"
                    data-testid="scroll-right"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-700" />
                  </button>
                </div>
              )}

              {/* No containers message */}
              {containers.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-white/80 text-sm">New container opening soon! Be the first to pledge.</p>
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <div className="space-y-3 mb-12">
            <Link to="/products">
              <Button 
                className="w-full h-16 text-xl font-bold rounded-2xl bg-gradient-to-r from-primary to-blue-500 hover:shadow-2xl transition-all"
                data-testid="pledge-material-button"
              >
                Pledge Material <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
            </Link>
            <p className="text-center text-sm text-gray-600">
              30% deposit to secure your order. 70% due upon production completion. Production starts at 80% capacity.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-3xl font-bold text-primary mb-2">$1.23</div>
              <div className="text-sm text-gray-600">LVP per sqft</div>
              <div className="text-xs text-gray-500 mt-1">Elite member pricing</div>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-3xl font-bold text-primary mb-2">$1.98</div>
              <div className="text-sm text-gray-600">Hardwood per sqft</div>
              <div className="text-xs text-gray-500 mt-1">Elite member pricing</div>
            </div>
            <div className="text-center p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="text-3xl font-bold text-primary mb-2">18-30</div>
              <div className="text-sm text-gray-600">Days shipping</div>
              <div className="text-xs text-gray-500 mt-1">China to USA</div>
            </div>
          </div>
        </div>
      </section>

      {/* What is a Pledge */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-blue-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            What is a Pledge?
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            A <strong>pledge</strong> is your commitment to purchase a specific amount of flooring (minimum 1,500 sqft). 
            Pay <strong className="text-primary">30% deposit</strong> to secure your order, with the remaining <strong>70% due upon production completion</strong>.
            Production begins once the container reaches 80% capacity — or you can choose to ship immediately for faster delivery.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            How It Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary rounded-full mb-6">
                <Package className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                1
              </div>
              <h3 className="font-bold text-2xl mb-4">Choose Your Flooring</h3>
              <p className="text-gray-600 leading-relaxed">
                Select LVP or Engineered Hardwood + color.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500 rounded-full mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                2
              </div>
              <h3 className="font-bold text-2xl mb-4">Pledge Your SQFT</h3>
              <p className="text-gray-600 leading-relaxed">
                Minimum 1,500 sq ft. Your pledge adds to this container.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600 rounded-full mb-6">
                <Ship className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                3
              </div>
              <h3 className="font-bold text-2xl mb-4">Container Ships When Full</h3>
              <p className="text-gray-600 leading-relaxed">
                Once capacity is hit, your order is finalized & shipped.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Save on Your Next Project?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Join thousands of builders getting wholesale prices on premium materials.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/products">
              <Button
                className="h-14 px-12 text-lg font-semibold rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all"
              >
                Browse Products <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/membership">
              <Button
                variant="outline"
                className="h-14 px-12 text-lg font-semibold rounded-full border-2 border-primary text-primary hover:bg-primary/10 shadow-lg hover:shadow-xl transition-all"
              >
                View Member Benefits
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center mb-6">
            <img src="/bluecrate-logo-cropped.jpg" alt="BlueCrate" className="h-12" />
          </div>
          <div className="flex justify-center space-x-8 mb-6">
            <Link to="/faq" className="text-sm text-gray-600 hover:text-primary transition-colors">FAQ</Link>
            <Link to="/contact" className="text-sm text-gray-600 hover:text-primary transition-colors">Contact</Link>
            <Link to="/membership" className="text-sm text-gray-600 hover:text-primary transition-colors">Membership</Link>
          </div>
          <p className="text-sm text-gray-600 text-center">
            © 2025 BlueCrate Group. Premium building materials through cooperative shipping.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
